import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { isoUTCToLocal } from '../../../utils/formatDate';

export interface ExamByCodeFormData {
    // First tab
    name: string;
    description: string;
    startDate: string;
    maxAttempts: number;
    timer: number;
    rebaseValue: number;

    // Second tab
    questionnaire: string;
    questionnaireWithKeys: string;

    // Third tab
    numberOfQuestions: number;
    answerKey: string[];
}

type UseExamByCodeFormProps = {
    initialExamByCodeFormData: ExamByCodeFormData;
    debounceDelay?: number;
};

const normalizeFormData = (data: ExamByCodeFormData): ExamByCodeFormData => ({
    name: data.name ?? '',
    description: data.description ?? '',
    startDate: isoUTCToLocal(data.startDate),
    maxAttempts: data.maxAttempts ?? 0,
    timer: data.timer ?? 0,
    rebaseValue: data.rebaseValue ?? 0,
    questionnaire: data.questionnaire ?? '',
    questionnaireWithKeys: data.questionnaireWithKeys ?? '',
    numberOfQuestions: data.numberOfQuestions ?? 0,
    answerKey: data.answerKey ?? [],
});

export function useExamByCodeForm({ initialExamByCodeFormData, debounceDelay = 300 }: UseExamByCodeFormProps) {
    // Normalize ONCE for first render
    const normalizedInitial = normalizeFormData(initialExamByCodeFormData);

    // Form data state from the api call
    const [examByCodeFormData, setExamByCodeFormData] = useState<ExamByCodeFormData>(normalizedInitial);

    // Raw form debouncing and data or the dirty form data once the user
    // filled out information in textfields
    const [rawExamByCodeFormData, setRawExamByCodeFormData] = useState<ExamByCodeFormData>(normalizedInitial);
    const [debouncedFormData] = useDebounce(rawExamByCodeFormData, debounceDelay);

    // Touch fields recording to handle error textfield validation
    const [examByCodeTouchedFields, setExamByCodeTouchedFields] = useState<Record<keyof ExamByCodeFormData, boolean>>({
        name: false,
        description: false,
        startDate: false,
        maxAttempts: false,
        timer: false,
        rebaseValue: false,
        questionnaire: false,
        questionnaireWithKeys: false,
        numberOfQuestions: false,
        answerKey: false
    });

    useEffect(() => {
        const normalized = normalizeFormData(initialExamByCodeFormData);
        setRawExamByCodeFormData(normalized);
        setExamByCodeFormData(normalized);
        setExamByCodeTouchedFields({
            name: false,
            description: false,
            startDate: false,
            maxAttempts: false,
            timer: false,
            rebaseValue: false,
            questionnaire: false,
            questionnaireWithKeys: false,
            numberOfQuestions: false,
            answerKey: false
        });
    }, [initialExamByCodeFormData]);

    // Debouncing mechanism for form state (create / edit)
    useEffect(() => {
        setExamByCodeFormData(debouncedFormData);
    }, [debouncedFormData]);

    useEffect(() => {
        const count = rawExamByCodeFormData.numberOfQuestions || 0;

        if (count <= 0) return;

        setRawExamByCodeFormData((prev: any) => {
            const current = prev.answerKey || [];

            if (current.length === count) return prev;

            return {
                ...prev,
                answerKey: Array.from({ length: count }, (_, i) => current[i] || ''),
            };
        });
    }, [rawExamByCodeFormData.numberOfQuestions]);


    const numberFields: (keyof ExamByCodeFormData)[] = [
        'maxAttempts',
        'timer',
        'rebaseValue',
        'numberOfQuestions',
    ];

    const handleExamByCodeChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value } = e.target;
            const key = name as keyof ExamByCodeFormData;

            setRawExamByCodeFormData((prev) => ({
                ...prev,
                [key]: numberFields.includes(key)
                    ? value === '' ? 0 : Number(value)
                    : value,
            }));
        },
        [],
    );

    const handleExamByCodeBlur = useCallback(
        (field: keyof ExamByCodeFormData) => {
            setExamByCodeTouchedFields((prev) => ({ ...prev, [field]: true }));
        },
        [],
    );

    const resetExamByCodeForm = useCallback(
        (newInitialData?: ExamByCodeFormData) => {
            const normalized = normalizeFormData(
                newInitialData ?? initialExamByCodeFormData
            );

            setRawExamByCodeFormData(normalized);
            setExamByCodeFormData(normalized);

            setExamByCodeTouchedFields({
                name: false,
                description: false,
                startDate: false,
                maxAttempts: false,
                timer: false,
                rebaseValue: false,
                questionnaire: false,
                questionnaireWithKeys: false,
                numberOfQuestions: false,
                answerKey: false,
            });
        },
        [initialExamByCodeFormData]
    );

    const isExamByCodeFormValid = useCallback(() => {
        const {
            name,
            description,
            startDate,
            maxAttempts,
            timer,
            rebaseValue,
            questionnaire,
            questionnaireWithKeys,
            numberOfQuestions,
            answerKey,
        } = examByCodeFormData;

        return (
            // text fields
            !!name?.trim() &&
            !!description?.trim() &&
            !!startDate?.trim() &&

            // numeric fields
            Number.isInteger(maxAttempts) && maxAttempts > 0 &&
            Number.isInteger(timer) && timer >= 0 &&
            Number.isInteger(rebaseValue) && rebaseValue >= 0 &&
            Number.isInteger(numberOfQuestions) && numberOfQuestions > 0 &&

            // files / urls
            !!questionnaire?.trim() &&
            !!questionnaireWithKeys?.trim() &&

            // answer keys
            Array.isArray(answerKey) &&
            answerKey.length === numberOfQuestions &&
            answerKey.every(k => k.trim() !== '')
        );
    }, [examByCodeFormData]);

    return {
        examByCodeFormData,
        rawExamByCodeFormData,
        setRawExamByCodeFormData,
        setExamByCodeFormData,
        examByCodeTouchedFields,
        setExamByCodeTouchedFields,
        handleExamByCodeChange,
        handleExamByCodeBlur,
        resetExamByCodeForm,
        isExamByCodeFormValid,
    };
}
